import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import {
  ArrowLeft, Shield, Users, FileText, AlertTriangle, DollarSign,
  CheckCircle, XCircle, ChevronRight, Search, Filter, Clock,
  TrendingUp, Building, RefreshCw, Loader2, Eye
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DISPUTE_STATUS = {
  open: { label: "Aberto", color: "bg-red-100 text-red-700" },
  investigating: { label: "Investigando", color: "bg-amber-100 text-amber-700" },
  resolved: { label: "Resolvido", color: "bg-emerald-100 text-emerald-700" },
  escalated: { label: "Escalado", color: "bg-purple-100 text-purple-700" }
};

const RESOLUTION_LEVELS = {
  n1_ai: { label: "N1 - IA", color: "bg-blue-100 text-blue-700" },
  n2_expert: { label: "N2 - Especialista", color: "bg-amber-100 text-amber-700" },
  n3_arbitration: { label: "N3 - Arbitragem", color: "bg-red-100 text-red-700" }
};

export default function AdminPage() {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("overview");
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [disputes, setDisputes] = useState([]);
  const [providers, setProviders] = useState([]);
  const [escrows, setEscrows] = useState([]);
  const [selectedDispute, setSelectedDispute] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    if (user?.role !== "admin") {
      toast.error("Acesso restrito a administradores");
      navigate("/");
      return;
    }
    fetchData();
  }, [token, user, navigate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [statsRes, disputesRes, providersRes, escrowsRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/stats`, { headers }),
        fetch(`${API_URL}/api/admin/disputes`, { headers }),
        fetch(`${API_URL}/api/admin/providers`, { headers }),
        fetch(`${API_URL}/api/admin/escrows`, { headers })
      ]);
      
      if (statsRes.ok) setStats(await statsRes.json());
      if (disputesRes.ok) setDisputes(await disputesRes.json());
      if (providersRes.ok) setProviders(await providersRes.json());
      if (escrowsRes.ok) setEscrows(await escrowsRes.json());
    } catch (error) {
      console.error("Error:", error);
      toast.error("Erro ao carregar dados");
    } finally {
      setLoading(false);
    }
  };

  const handleEscalateDispute = async (disputeId, reason) => {
    setActionLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/disputes/${disputeId}/escalate`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ reason })
      });
      if (res.ok) {
        toast.success("Disputa escalada com sucesso");
        fetchData();
        setSelectedDispute(null);
      }
    } catch (error) {
      toast.error("Erro ao escalar disputa");
    } finally {
      setActionLoading(false);
    }
  };

  const handleResolveDispute = async (disputeId, resolution, refundPercentage, notes) => {
    setActionLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/disputes/${disputeId}/resolve`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ resolution, refund_percentage: refundPercentage, notes })
      });
      if (res.ok) {
        toast.success("Disputa resolvida com sucesso");
        fetchData();
        setSelectedDispute(null);
      }
    } catch (error) {
      toast.error("Erro ao resolver disputa");
    } finally {
      setActionLoading(false);
    }
  };

  const handleVerifyProvider = async (providerId, verified) => {
    setActionLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/providers/${providerId}/verify`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ verified })
      });
      if (res.ok) {
        toast.success(verified ? "Prestador verificado" : "Verificação removida");
        fetchData();
      }
    } catch (error) {
      toast.error("Erro ao atualizar prestador");
    } finally {
      setActionLoading(false);
    }
  };

  const handleSuspendProvider = async (providerId, suspend, reason) => {
    setActionLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/providers/${providerId}/suspend`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ suspend, reason })
      });
      if (res.ok) {
        toast.success(suspend ? "Prestador suspenso" : "Suspensão removida");
        fetchData();
      }
    } catch (error) {
      toast.error("Erro ao atualizar prestador");
    } finally {
      setActionLoading(false);
    }
  };

  const handleReleaseEscrow = async (escrowId, amount, notes) => {
    setActionLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/escrows/${escrowId}/release`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ amount, notes })
      });
      if (res.ok) {
        toast.success("Pagamento liberado com sucesso");
        fetchData();
      }
    } catch (error) {
      toast.error("Erro ao liberar pagamento");
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen-mobile flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-neutral-200 border-t-neutral-900 rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen-mobile bg-white pb-8" data-testid="admin-page">
      {/* Header */}
      <header className="sticky-header border-b border-neutral-100 bg-neutral-900 text-white">
        <div className="px-5 h-14 flex items-center gap-3">
          <Shield className="w-6 h-6" />
          <div className="flex-1">
            <h1 className="font-semibold">Painel Admin</h1>
            <p className="text-xs text-neutral-400">eZelo.</p>
          </div>
          <Button onClick={fetchData} className="btn-minimal bg-neutral-800 text-white h-9 px-3">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </header>

      {/* Tab Navigation */}
      <div className="px-5 py-3 border-b border-neutral-100 bg-white sticky top-14 z-40">
        <div className="flex gap-2 overflow-x-auto scrollbar-hide">
          {[
            { id: "overview", label: "Visão Geral", icon: TrendingUp },
            { id: "disputes", label: "Disputas", icon: AlertTriangle },
            { id: "providers", label: "Prestadores", icon: Users },
            { id: "escrows", label: "Pagamentos", icon: DollarSign }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                activeTab === tab.id ? "bg-neutral-900 text-white" : "bg-neutral-100 text-neutral-600"
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <main className="px-5 py-6">
        {/* Overview Tab */}
        {activeTab === "overview" && stats && (
          <div className="space-y-6 animate-up">
            {/* Main Stats */}
            <div className="grid grid-cols-2 gap-3">
              <div className="card-dark p-4">
                <Users className="w-5 h-5 text-neutral-400 mb-2" />
                <p className="text-2xl font-semibold text-white">{stats.users?.total || 0}</p>
                <p className="text-sm text-neutral-400">Usuários</p>
              </div>
              <div className="card-dark p-4">
                <FileText className="w-5 h-5 text-neutral-400 mb-2" />
                <p className="text-2xl font-semibold text-white">{stats.contracts?.total || 0}</p>
                <p className="text-sm text-neutral-400">Contratos</p>
              </div>
            </div>

            {/* Disputes Alert */}
            {stats.disputes?.open > 0 && (
              <div className="card-clean p-4 border-red-200 bg-red-50">
                <div className="flex items-center gap-3">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                  <div className="flex-1">
                    <p className="font-medium text-red-900">{stats.disputes.open} disputas abertas</p>
                    <p className="text-sm text-red-700">Requerem atenção</p>
                  </div>
                  <Button 
                    onClick={() => setActiveTab("disputes")}
                    className="btn-minimal bg-red-600 text-white h-9 px-4"
                  >
                    Ver
                  </Button>
                </div>
              </div>
            )}

            {/* Service Stats */}
            <div className="card-subtle p-4">
              <h3 className="font-medium text-neutral-900 mb-4">Serviços</h3>
              <div className="grid grid-cols-4 gap-2 text-center">
                <div>
                  <p className="text-lg font-semibold text-neutral-900">{stats.services?.pending || 0}</p>
                  <p className="text-xs text-neutral-500">Pendentes</p>
                </div>
                <div>
                  <p className="text-lg font-semibold text-blue-600">{stats.services?.in_progress || 0}</p>
                  <p className="text-xs text-neutral-500">Em Progresso</p>
                </div>
                <div>
                  <p className="text-lg font-semibold text-emerald-600">{stats.services?.completed || 0}</p>
                  <p className="text-xs text-neutral-500">Concluídos</p>
                </div>
                <div>
                  <p className="text-lg font-semibold text-red-600">{stats.contracts?.disputed || 0}</p>
                  <p className="text-xs text-neutral-500">Disputados</p>
                </div>
              </div>
            </div>

            {/* Financial Stats */}
            <div className="card-subtle p-4">
              <h3 className="font-medium text-neutral-900 mb-4">Financeiro</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-neutral-600">Total em Escrow</span>
                  <span className="font-semibold">R$ {(stats.financials?.total_escrow_value || 0).toLocaleString("pt-BR")}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-neutral-600">Liberado</span>
                  <span className="font-semibold text-emerald-600">R$ {(stats.financials?.total_released || 0).toLocaleString("pt-BR")}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-neutral-600">Retido</span>
                  <span className="font-semibold text-amber-600">R$ {(stats.financials?.total_retained || 0).toLocaleString("pt-BR")}</span>
                </div>
              </div>
            </div>

            {/* Provider Stats */}
            <div className="card-subtle p-4">
              <h3 className="font-medium text-neutral-900 mb-4">Prestadores</h3>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div>
                  <p className="text-lg font-semibold text-neutral-900">{stats.providers?.total || 0}</p>
                  <p className="text-xs text-neutral-500">Total</p>
                </div>
                <div>
                  <p className="text-lg font-semibold text-emerald-600">{stats.providers?.verified || 0}</p>
                  <p className="text-xs text-neutral-500">Verificados</p>
                </div>
                <div>
                  <p className="text-lg font-semibold text-blue-600">{stats.providers?.active || 0}</p>
                  <p className="text-xs text-neutral-500">Ativos</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Disputes Tab */}
        {activeTab === "disputes" && (
          <div className="space-y-4 animate-up">
            <div className="flex items-center justify-between">
              <h2 className="font-semibold text-neutral-900">Disputas</h2>
              <span className="badge-minimal">{disputes.length}</span>
            </div>

            {disputes.length === 0 ? (
              <div className="card-subtle p-8 text-center">
                <CheckCircle className="w-12 h-12 text-emerald-300 mx-auto mb-4" />
                <p className="font-medium text-neutral-900">Nenhuma disputa</p>
                <p className="text-sm text-neutral-500">Todas as disputas foram resolvidas</p>
              </div>
            ) : (
              disputes.map((dispute) => {
                const status = DISPUTE_STATUS[dispute.status] || DISPUTE_STATUS.open;
                const level = RESOLUTION_LEVELS[dispute.resolution_level] || RESOLUTION_LEVELS.n1_ai;
                return (
                  <div key={dispute.dispute_id} className="card-clean p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <p className="font-medium text-neutral-900">#{dispute.dispute_id.slice(-8)}</p>
                        <p className="text-sm text-neutral-500">{dispute.reason}</p>
                      </div>
                      <div className="flex gap-2">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${level.color}`}>
                          {level.label}
                        </span>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${status.color}`}>
                          {status.label}
                        </span>
                      </div>
                    </div>
                    
                    <p className="text-sm text-neutral-700 mb-3">{dispute.description}</p>
                    
                    {dispute.raised_by_user && (
                      <p className="text-xs text-neutral-500 mb-3">
                        Aberto por: {dispute.raised_by_user.name} ({dispute.raised_by_user.email})
                      </p>
                    )}

                    {dispute.status !== "resolved" && (
                      <div className="flex gap-2 pt-3 border-t border-neutral-100">
                        {dispute.resolution_level !== "n3_arbitration" && (
                          <Button
                            onClick={() => handleEscalateDispute(dispute.dispute_id, "Escalado pelo admin")}
                            disabled={actionLoading}
                            className="btn-minimal btn-secondary flex-1 h-9 text-sm"
                          >
                            Escalar
                          </Button>
                        )}
                        <Button
                          onClick={() => setSelectedDispute(dispute)}
                          className="btn-minimal btn-primary flex-1 h-9 text-sm"
                        >
                          Resolver
                        </Button>
                      </div>
                    )}

                    {dispute.final_resolution && (
                      <div className="mt-3 p-3 bg-emerald-50 rounded-lg">
                        <p className="text-sm font-medium text-emerald-900">
                          Resolução: {dispute.final_resolution.resolution}
                        </p>
                        {dispute.final_resolution.notes && (
                          <p className="text-xs text-emerald-700 mt-1">{dispute.final_resolution.notes}</p>
                        )}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        )}

        {/* Providers Tab */}
        {activeTab === "providers" && (
          <div className="space-y-4 animate-up">
            <div className="flex items-center justify-between">
              <h2 className="font-semibold text-neutral-900">Prestadores</h2>
              <span className="badge-minimal">{providers.length}</span>
            </div>

            {providers.map((provider) => (
              <div key={provider.provider_id} className="card-clean p-4">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <p className="font-medium text-neutral-900">{provider.user?.name || "---"}</p>
                    <p className="text-sm text-neutral-500">{provider.user?.email}</p>
                  </div>
                  <div className="flex gap-2">
                    {provider.is_verified && (
                      <span className="badge-success text-xs">Verificado</span>
                    )}
                    {!provider.is_active && (
                      <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
                        Suspenso
                      </span>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-2 text-center mb-3">
                  <div>
                    <p className="font-semibold text-neutral-900">{provider.total_jobs || 0}</p>
                    <p className="text-xs text-neutral-500">Trabalhos</p>
                  </div>
                  <div>
                    <p className="font-semibold text-neutral-900">{provider.average_rating?.toFixed(1) || "0.0"}</p>
                    <p className="text-xs text-neutral-500">Avaliação</p>
                  </div>
                  <div>
                    <p className="font-semibold text-neutral-900">{provider.total_reviews || 0}</p>
                    <p className="text-xs text-neutral-500">Reviews</p>
                  </div>
                </div>

                <div className="flex gap-2 pt-3 border-t border-neutral-100">
                  <Button
                    onClick={() => handleVerifyProvider(provider.provider_id, !provider.is_verified)}
                    disabled={actionLoading}
                    className={`btn-minimal flex-1 h-9 text-sm ${
                      provider.is_verified ? "btn-secondary" : "btn-primary"
                    }`}
                  >
                    {provider.is_verified ? "Remover Verificação" : "Verificar"}
                  </Button>
                  <Button
                    onClick={() => handleSuspendProvider(provider.provider_id, provider.is_active, "Ação administrativa")}
                    disabled={actionLoading}
                    className={`btn-minimal flex-1 h-9 text-sm ${
                      provider.is_active ? "bg-red-100 text-red-700 hover:bg-red-200" : "btn-secondary"
                    }`}
                  >
                    {provider.is_active ? "Suspender" : "Ativar"}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Escrows Tab */}
        {activeTab === "escrows" && (
          <div className="space-y-4 animate-up">
            <div className="flex items-center justify-between">
              <h2 className="font-semibold text-neutral-900">Pagamentos (Escrow)</h2>
              <span className="badge-minimal">{escrows.length}</span>
            </div>

            {escrows.map((escrow) => {
              const progress = escrow.total_amount > 0 
                ? Math.round((escrow.released_amount / escrow.total_amount) * 100) 
                : 0;
              return (
                <div key={escrow.escrow_id} className="card-clean p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <p className="font-medium text-neutral-900">#{escrow.escrow_id.slice(-8)}</p>
                      <p className="text-sm text-neutral-500">Contrato: {escrow.contract_id?.slice(-8)}</p>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      escrow.status === "pending" ? "bg-neutral-100 text-neutral-700" :
                      escrow.status === "funded" ? "bg-blue-100 text-blue-700" :
                      escrow.status === "disputed" ? "bg-red-100 text-red-700" :
                      escrow.status === "completed" ? "bg-emerald-100 text-emerald-700" :
                      "bg-amber-100 text-amber-700"
                    }`}>
                      {escrow.status}
                    </span>
                  </div>

                  <div className="mb-3">
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-neutral-500">Liberado</span>
                      <span className="font-medium">{progress}%</span>
                    </div>
                    <div className="h-2 bg-neutral-100 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-emerald-500 rounded-full transition-all"
                        style={{ width: `${progress}%` }}
                      />
                    </div>
                    <div className="flex justify-between text-xs text-neutral-500 mt-1">
                      <span>R$ {(escrow.released_amount || 0).toLocaleString("pt-BR")}</span>
                      <span>R$ {(escrow.total_amount || 0).toLocaleString("pt-BR")}</span>
                    </div>
                  </div>

                  {escrow.status !== "completed" && escrow.status !== "refunded" && (
                    <Button
                      onClick={() => handleReleaseEscrow(
                        escrow.escrow_id, 
                        escrow.total_amount - escrow.released_amount,
                        "Liberação manual pelo admin"
                      )}
                      disabled={actionLoading}
                      className="btn-minimal btn-primary w-full h-9 text-sm"
                    >
                      <DollarSign className="w-4 h-4 mr-2" />
                      Liberar Restante
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </main>

      {/* Resolve Dispute Modal */}
      {selectedDispute && (
        <ResolveDisputeModal
          dispute={selectedDispute}
          onClose={() => setSelectedDispute(null)}
          onResolve={handleResolveDispute}
          loading={actionLoading}
        />
      )}
    </div>
  );
}

function ResolveDisputeModal({ dispute, onClose, onResolve, loading }) {
  const [resolution, setResolution] = useState("");
  const [refundPercentage, setRefundPercentage] = useState(0);
  const [notes, setNotes] = useState("");

  const resolutionOptions = [
    { value: "provider_favor", label: "Favor do Prestador", desc: "Serviço foi entregue corretamente" },
    { value: "refund_partial", label: "Reembolso Parcial", desc: "Cliente recebe parte do valor" },
    { value: "refund_full", label: "Reembolso Total", desc: "Cliente recebe 100% de volta" },
    { value: "rework", label: "Retrabalho", desc: "Prestador deve refazer o serviço" }
  ];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end justify-center z-50">
      <div className="bg-white w-full max-w-lg rounded-t-2xl p-6 animate-up max-h-[80vh] overflow-y-auto">
        <h3 className="font-semibold text-lg text-neutral-900 mb-4">Resolver Disputa</h3>
        <p className="text-sm text-neutral-500 mb-6">#{dispute.dispute_id.slice(-8)} - {dispute.reason}</p>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">Resolução</label>
            {resolutionOptions.map((opt) => (
              <button
                key={opt.value}
                onClick={() => setResolution(opt.value)}
                className={`w-full text-left p-3 rounded-lg border-2 mb-2 transition-colors ${
                  resolution === opt.value
                    ? "border-neutral-900 bg-neutral-50"
                    : "border-neutral-200 hover:border-neutral-300"
                }`}
              >
                <p className="font-medium text-neutral-900">{opt.label}</p>
                <p className="text-sm text-neutral-500">{opt.desc}</p>
              </button>
            ))}
          </div>

          {(resolution === "refund_partial" || resolution === "refund_full") && (
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Percentual de Reembolso: {refundPercentage}%
              </label>
              <input
                type="range"
                min={0}
                max={100}
                value={refundPercentage}
                onChange={(e) => setRefundPercentage(parseInt(e.target.value))}
                className="w-full"
                disabled={resolution === "refund_full"}
              />
              {resolution === "refund_full" && setRefundPercentage(100)}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">Notas</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Justificativa da decisão..."
              className="input-minimal h-24 resize-none py-3"
            />
          </div>
        </div>

        <div className="flex gap-3 mt-6">
          <Button onClick={onClose} className="btn-minimal btn-secondary flex-1">
            Cancelar
          </Button>
          <Button
            onClick={() => onResolve(dispute.dispute_id, resolution, refundPercentage, notes)}
            disabled={loading || !resolution}
            className="btn-minimal btn-primary flex-1"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Confirmar"}
          </Button>
        </div>
      </div>
    </div>
  );
}
